import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Check, Clock, User, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { DailyBrief, dailyBriefsRef, updateDocument, Agency } from '../lib/firebase';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export default function InfluencerBrief() {
  const { influencerSlug } = useParams();
  const [allBriefs, setAllBriefs] = useState<DailyBrief[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [influencerName, setInfluencerName] = useState<string>('');
  const [agencyName, setAgencyName] = useState<string>('Le Suivi');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBriefs();
  }, [influencerSlug]);

  const loadBriefs = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!influencerSlug) {
        setError('Lien invalide');
        return;
      }

      console.log('Searching for influencer slug:', influencerSlug);

      // Récupérer tous les briefs pour cet influenceur
      const briefsQuery = query(
        collection(db, 'dailyBriefs'),
        where('influencerSlug', '==', influencerSlug)
      );

      const briefsSnapshot = await getDocs(briefsQuery);
      console.log('Found briefs:', briefsSnapshot.docs.length);
      
      if (briefsSnapshot.empty) {
        // Try to find briefs with similar slug (case insensitive)
        const allBriefsQuery = query(collection(db, 'dailyBriefs'));
        const allBriefsSnapshot = await getDocs(allBriefsQuery);
        
        const allBriefs = allBriefsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as DailyBrief[];
        
        console.log('All briefs in database:', allBriefs.map(b => ({ 
          slug: b.influencerSlug, 
          name: b.influencerName,
          date: b.date 
        })));
        
        // Try case-insensitive match
        const matchingBriefs = allBriefs.filter(brief => 
          brief.influencerSlug?.toLowerCase() === influencerSlug.toLowerCase()
        );
        
        if (matchingBriefs.length > 0) {
          setAllBriefs(matchingBriefs);
          setInfluencerName(matchingBriefs[0].influencerName);
          setAgencyName(matchingBriefs[0].agencyName || 'Le Suivi');
          // Commencer par la date du jour
          const today = new Date();
          setCurrentDate(today);
          setSelectedDate(today);
          return;
        }
        
        setError(`Aucun brief trouvé pour "${influencerSlug}". Vérifiez que le lien est correct.`);
        return;
      }

      const briefsData = briefsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DailyBrief[];

      console.log('Briefs data:', briefsData);

      setAllBriefs(briefsData);
      if (briefsData.length > 0) {
        setInfluencerName(briefsData[0].influencerName);
        setAgencyName(briefsData[0].agencyName || 'Le Suivi');
      }
      
      // Commencer par la date du jour
      const today = new Date();
      setCurrentDate(today);
      setSelectedDate(today);

    } catch (error) {
      console.error('Error loading briefs:', error);
      setError('Erreur lors du chargement du brief');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskToggle = async (briefId: string, taskId: string) => {
    try {
      const briefToUpdate = allBriefs.find(b => b.id === briefId);
      if (!briefToUpdate) return;

      const updatedTasks = briefToUpdate.tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            completed: !task.completed,
            completedAt: !task.completed ? new Date() : null
          };
        }
        return task;
      });

      // Update in Firestore
      const briefRef = doc(db, 'dailyBriefs', briefId);
      await updateDoc(briefRef, {
        tasks: updatedTasks,
        updatedAt: new Date()
      });

      // Update local state
      setAllBriefs(prev => prev.map(brief => 
        brief.id === briefId 
          ? { ...brief, tasks: updatedTasks }
          : brief
      ));

    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSameDay = (date1: Date | { seconds: number }, date2: Date) => {
    const d1 = 'seconds' in date1 ? new Date(date1.seconds * 1000) : date1;
    return d1.toDateString() === date2.toDateString();
  };

  const getBriefsForDate = (date: Date) => {
    return allBriefs.filter(brief => isSameDay(brief.date, date));
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Start from Monday
    
    const days = [];
    const currentDay = new Date(startDate);
    
    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  const previousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const selectedBriefs = getBriefsForDate(selectedDate);
  const allTasks = selectedBriefs.flatMap(brief => brief.tasks);
  const completedTasks = allTasks.filter(task => task.completed).length;
  const totalTasks = allTasks.length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const calendarDays = getCalendarDays();
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Brief non trouvé</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bonjour {influencerName} !</h1>
              <p className="text-gray-600">Gérez vos tâches quotidiennes</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">{formatDate(selectedDate)}</span>
            </div>
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              Aujourd'hui
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendrier */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={previousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* En-têtes des jours */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Grille du calendrier */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                const dayBriefs = getBriefsForDate(day);
                const hasTasks = dayBriefs.length > 0;
                const dayTasks = dayBriefs.flatMap(brief => brief.tasks);
                const completedDayTasks = dayTasks.filter(task => task.completed).length;
                const allDayTasksCompleted = dayTasks.length > 0 && completedDayTasks === dayTasks.length;

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(new Date(day))}
                    className={`
                      relative p-2 h-12 text-sm font-medium rounded-lg transition-all
                      ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                      ${isToday ? 'bg-purple-100 text-purple-700 font-bold' : ''}
                      ${isSelected ? 'bg-purple-600 text-white' : 'hover:bg-gray-100'}
                      ${hasTasks && !isSelected ? 'bg-blue-50 border-2 border-blue-200' : ''}
                    `}
                  >
                    {day.getDate()}
                    {hasTasks && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                        <div className={`w-2 h-2 rounded-full ${
                          allDayTasksCompleted ? 'bg-green-500' : 'bg-orange-400'
                        }`} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Légende */}
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-400" />
                <span>Tâches en cours</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Tâches terminées</span>
              </div>
            </div>
          </div>

          {/* Tâches du jour sélectionné */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Tâches du {selectedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
              </h2>
              {totalTasks > 0 && (
                <div className="flex items-center gap-2">
                  <CheckCircle className={`w-5 h-5 ${
                    progressPercentage === 100 ? 'text-green-500' : 'text-gray-300'
                  }`} />
                  <span className="text-sm text-gray-600">
                    {completedTasks}/{totalTasks}
                  </span>
                </div>
              )}
            </div>

            {totalTasks > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progression</span>
                  <span className="text-sm text-gray-600">{Math.round(progressPercentage)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            )}

            {selectedBriefs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune tâche</h3>
                <p className="text-gray-600">Aucune tâche prévue pour cette date.</p>
              </div>
            ) : (
              <div className="space-y-6 max-h-96 overflow-y-auto">
                {selectedBriefs.map((brief, briefIndex) => (
                  <div key={brief.id}>
                    {selectedBriefs.length > 1 && (
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">
                        Brief {briefIndex + 1}
                      </h3>
                    )}
                    
                    {brief.tasks.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-gray-600">Aucune tâche dans ce brief</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {brief.tasks.map((task) => (
                          <div
                            key={task.id}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                              task.completed
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-gray-50 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                            }`}
                            onClick={() => handleTaskToggle(brief.id!, task.id)}
                          >
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              task.completed
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300 hover:border-purple-500'
                            }`}>
                              {task.completed && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${task.completed ? 'line-through text-green-700' : 'text-gray-800'}`}>
                                {task.text}
                              </p>
                              {task.completed && task.completedAt && (
                                <p className="text-sm text-green-600 mt-1">
                                  Terminé à {formatTime(task.completedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            Powered by <span className="font-semibold text-purple-600">{agencyName}</span>
          </p>
        </div>
      </div>
    </div>
  );
}